import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCreditCard,
  faExclamationTriangle,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Flex from 'components/common/Flex';
import { usePayOrganizationInvoiceMutation } from 'rtk/org-admin/organizationBilling.slice';
import { formatUsdCents } from 'util/organizationBillingFormat';

const dataOf = value => value?.Data ?? value?.data ?? value;
const field = (value, name) =>
  value?.[name] ?? value?.[name[0].toLowerCase() + name.slice(1)];
const requestKey = () =>
  window.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const errorText = error =>
  error?.data?.Data ??
  error?.data?.data ??
  error?.error ??
  error?.message ??
  'Payment could not be completed.';

const formatMoney = cents => formatUsdCents(cents ?? 0);

export const OrganizationPayNowModal = ({
  show,
  onHide,
  invoice,
  cards = [],
  onSuccess
}) => {
  const [payInvoice, { isLoading: isSubmitting }] =
    usePayOrganizationInvoiceMutation();
  const [selectedCardGuid, setSelectedCardGuid] = useState(() => {
    const defaultCard = cards.find(c => field(c, 'IsDefault'));
    return defaultCard
      ? field(defaultCard, 'GUID')
      : cards[0]
      ? field(cards[0], 'GUID')
      : '';
  });
  const [authenticating3DS, setAuthenticating3DS] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const paymentRequestKey = useRef(requestKey());

  if (!invoice) return null;

  const invoiceGuid = field(invoice, 'GUID');
  const invoiceNumber = field(invoice, 'InvoiceNumber') || 'Invoice';
  const totalCents = field(invoice, 'TotalCents') ?? 0;
  const currency = field(invoice, 'Currency') || 'USD';

  const handlePay = async () => {
    setErrorMessage(null);
    try {
      const response = await payInvoice({
        invoiceGuid,
        paymentMethodGuid: selectedCardGuid || null,
        idempotencyKey: paymentRequestKey.current
      }).unwrap();

      const result = dataOf(response);
      const status = field(result, 'Status');
      const isSettled = field(result, 'IsSettled');
      const clientSecret = field(result, 'ClientSecret');
      const publishableKey = field(result, 'PublishableKey');

      if (isSettled === true) {
        toast.success(`Invoice ${invoiceNumber} settled successfully.`);
        if (onSuccess) await onSuccess(result);
        onHide();
        return;
      }

      if (status === 'RequiresAction' && clientSecret) {
        setAuthenticating3DS(true);
        try {
          const stripe = await loadStripe(publishableKey);
          if (!stripe) {
            throw new Error(
              'Unable to initialize payment verification with Stripe.'
            );
          }

          const { error: stripeError, paymentIntent } =
            await stripe.confirmCardPayment(clientSecret);
          if (stripeError) {
            setErrorMessage(
              stripeError.message || 'Payment authentication failed.'
            );
            toast.error(
              stripeError.message || '3D Secure authentication failed.'
            );
          } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            toast.success(
              'Payment verification completed. Checking invoice settlement.'
            );
            if (onSuccess) await onSuccess(result);
            onHide();
          } else {
            setErrorMessage(
              `Payment status: ${
                paymentIntent?.status || 'Processing'
              }. Please verify your card.`
            );
          }
        } finally {
          setAuthenticating3DS(false);
        }
        return;
      }

      if (status === 'Succeeded') {
        toast.info('Payment is confirmed. Checking invoice settlement.');
        if (onSuccess) await onSuccess(result);
        onHide();
        return;
      }
      if (status === 'Pending') {
        setErrorMessage(
          'Payment is processing. Refresh the invoice status before retrying.'
        );
        if (onSuccess) {
          await onSuccess(result);
          onHide();
        }
        return;
      }
      if (!['Failed', 'RetryScheduled', 'Cancelled'].includes(status)) {
        setErrorMessage(
          'Payment response is incomplete. Refresh the invoice before retrying.'
        );
        return;
      }
      paymentRequestKey.current = requestKey();
      const failureMsg =
        field(result, 'FailureMessage') ||
        'The card was declined or payment failed.';
      setErrorMessage(failureMsg);
      toast.error(failureMsg);
    } catch (error) {
      const msg = errorText(error);
      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  const isBusy = isSubmitting || authenticating3DS;

  return (
    <Modal
      show={show}
      onHide={isBusy ? undefined : onHide}
      size="md"
      centered
      backdrop={isBusy ? 'static' : true}
      keyboard={!isBusy}
    >
      <Modal.Header closeButton={!isBusy}>
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faCreditCard} className="me-2 text-primary" />
          Pay Invoice
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center py-3 bg-light rounded mb-3">
          <div className="text-muted fs--1 text-uppercase fw-semi-bold">
            Total Amount Due
          </div>
          <div className="fs-3 fw-bold text-dark">
            {formatMoney(totalCents, currency)}
          </div>
          <div className="text-secondary fs--1 mt-1">
            Invoice: {invoiceNumber}
          </div>
        </div>

        {errorMessage && (
          <Alert variant="danger" className="d-flex align-items-center py-2">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="me-2 flex-shrink-0"
            />
            <div className="fs--1">{errorMessage}</div>
          </Alert>
        )}

        {cards.length > 0 ? (
          <Form.Group className="mb-3">
            <Form.Label className="fs--1 fw-semi-bold">
              Select Payment Card
            </Form.Label>
            <Form.Select
              value={selectedCardGuid}
              onChange={e => setSelectedCardGuid(e.target.value)}
              disabled={isBusy}
            >
              {cards.map(card => {
                const guid = field(card, 'GUID');
                const last4 = field(card, 'Last4') || '****';
                const cardType = (
                  field(card, 'CardType') || 'Card'
                ).toUpperCase();
                const isDefault = field(card, 'IsDefault');
                const isBackup = field(card, 'IsBackup');
                const roleLabel = isDefault
                  ? ' (Primary)'
                  : isBackup
                  ? ' (Backup)'
                  : '';
                return (
                  <option key={guid} value={guid}>
                    {cardType} ending in {last4}
                    {roleLabel}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        ) : (
          <Alert variant="warning" className="fs--1">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            No saved cards found. The active default payment method on your
            account will be charged.
          </Alert>
        )}

        {authenticating3DS && (
          <div className="text-center py-2 text-info">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="fs--1 fw-semi-bold">
              Completing 3D Secure bank verification...
            </span>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={isBusy}
          size="sm"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handlePay}
          disabled={isBusy}
          size="sm"
          className="d-flex align-items-center"
        >
          {isBusy ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {authenticating3DS ? 'Verifying 3DS...' : 'Processing...'}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faLock} className="me-2" />
              Pay {formatMoney(totalCents, currency)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

OrganizationPayNowModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  invoice: PropTypes.object,
  cards: PropTypes.array,
  onSuccess: PropTypes.func
};

export default OrganizationPayNowModal;
